import dynamic from "next/dynamic";
import { AnnotationState } from "@markerjs/markerjs3";
import { popToastError } from "lib/actions/toast";
import { useViewsMode } from "lib/contexts/SpatialContexts/ViewsModeContext";
import { useCurrentProjectId } from "lib/hooks/useProject";
import { useViewImage } from "lib/hooks/views/useViewImage";
import { usePatchViewAnnotation } from "lib/hooks/views/usePatchViewAnnotation";
import LoadingSkeleton from "components/elements/Loading/LoadingSkeleton";

const Viewer = dynamic(
  () => import("components/elements/AnnotationViewer/AnnotationViewer"),
  { ssr: false },
);
const Editor = dynamic(
  () => import("components/elements/AnnotationViewer/AnnotationEditor"),
  { ssr: false },
);

export default function ViewSnapshotPanel() {
  const {
    loadedView,
    setLoadedView,
    isEditingAnnotations,
    stopEditingAnnotations,
  } = useViewsMode();
  const projectId = useCurrentProjectId();
  const saveAnnotation = usePatchViewAnnotation(projectId, loadedView);
  const { viewImageUrl } = useViewImage({
    projectId,
    snapshotId: loadedView?.snapshotId,
  });

  const annotationState = (loadedView?.snapshotAnnotationJson ?? undefined) as
    | AnnotationState
    | undefined;

  async function onSave(
    state: AnnotationState | null,
    annotatedSnapshotDataUrl?: string,
  ) {
    try {
      const result = await saveAnnotation(state, annotatedSnapshotDataUrl);
      if (result) {
        setLoadedView({
          ...loadedView,
          snapshotAnnotationJson: result.snapshotAnnotationJson,
          annotatedSnapshotId: result.annotatedSnapshotId,
        });
      }
      stopEditingAnnotations();
    } catch (error) {
      popToastError("There was a problem saving changes to view annotations.");
      console.error(error);
    }
  }

  return (
    <div className="absolute inset-0 bg-neutral-100">
      {!viewImageUrl && <LoadingSkeleton className="h-full w-full" />}
      {viewImageUrl && isEditingAnnotations && (
        <Editor
          initialAnnotationState={annotationState}
          targetImage={viewImageUrl}
          onSave={onSave}
          onClose={stopEditingAnnotations}
        />
      )}
      {viewImageUrl && !isEditingAnnotations && (
        <Viewer targetImage={viewImageUrl} annotationState={annotationState} />
      )}
    </div>
  );
}
